"use client";
import Link from "next/link";
import { useEffect, useState } from "react";
import { IconArrowRight } from "../icons";
import { useApplyTransition } from "./useApplyTransition";
import { GlobalThemeSwitch } from "./GlobalThemeSwitch";

/**
 * StickyApplyBar — fest verankerte Top-Leiste mit Mobile-Scroll-Trigger.
 *
 * Historie: Vorher zwei Varianten im Konflikt:
 *  1) Sehr früh: Header (relative) + Sticky-Bar (scrollY>600 fade-in)
 *     -> auf Mobile zwei "Jetzt bewerben"-CTAs ab scrollY>600.
 *  2) Ueberkorrektur danach: Sticky-Bar permanent fixed (kein Trigger).
 *     -> auf Mobile von Sekunde 0 doppelter CTA (Sticky "Jetzt bewerben"
 *     plus Hero "Bewerbung starten" beide above-the-fold).
 *
 * Jetzt: Desktop bleibt permanent sichtbar (Hero-CTA und Sticky-CTA stehen
 * dort nebeneinander/sequenziell auf grosser Flaeche, kein Konflikt).
 * Mobile (<= 720px) - User-Wunsch (2026-06-09 Late): die BAR (Wordmark +
 * Theme-Switch) ist IMMER sichtbar, damit Brand + Theme-Toggle stets
 * erreichbar sind. Nur der "Jetzt bewerben"-CTA wird scroll-getriggert
 * (initial versteckt, erscheint sobald Hero-CTA aus dem Viewport gescrollt
 * ist). So ist immer GENAU EIN Bewerben-CTA sichtbar, aber das Logo nicht
 * mehr "verschwunden" bis zum ersten Scroll.
 *
 * prefers-reduced-motion: instantaner Toggle (kein 200ms-Fade).
 *
 * Mitarbeiter-Bereich (Schloss) liegt im Footer. "Bereits beworben?" liegt
 * im Footer ("Bewerber-Akte"). Top-Leiste haelt sich auf den Apply-CTA
 * + Wordmark + Theme-Switch beschraenkt.
 *
 * Der "Jetzt bewerben"-CTA triggert nicht direkt eine Navigation, sondern
 * den Signature-Moment (Coin-Spin -> Crew-Pass -> /bewerben). Siehe
 * ApplyTransitionOverlay + useApplyTransition. href bleibt korrekt
 * gesetzt, damit "Open in new tab" und SEO-Crawler funktionieren.
 */
export function StickyApplyBar({ applyUrl, tenantSlug }: { applyUrl: string; tenantSlug?: string }) {
  const startApply = useApplyTransition(tenantSlug ? { tenantSlug } : {});

  // Query der aktuellen Seite (z. B. ?utm_source=indeed&standort=Hamburg)
  // an den Bewerben-Link anhaengen, damit der Bewerber nach dem Umweg
  // ueber die Startseite dieselbe Attribution/Prefill bekommt wie beim
  // Direktlink (Gruender 2026-08-03).
  const [effectiveHref, setEffectiveHref] = useState(applyUrl);
  useEffect(() => {
    const s = window.location.search;
    if (!s || s.length < 2 || !/^\/bewerben(?:$|[/?#])/.test(applyUrl)) {
      setEffectiveHref(applyUrl);
      return;
    }
    setEffectiveHref(applyUrl.includes("?") ? `${applyUrl}&${s.slice(1)}` : `${applyUrl}${s}`);
  }, [applyUrl]);

  // ctaVisible steuert nur den "Jetzt bewerben"-Link in der Bar.
  // Die Bar selbst (Wordmark + Theme-Switch) bleibt über dem Viewport-Top
  // durchgehend sichtbar, damit das Brand nicht "verschwunden" wirkt.
  // Default true für SSR + Desktop; Mobile-useEffect korrigiert auf
  // scrollY-basierten Wert.
  const [ctaVisible, setCtaVisible] = useState(true);

  useEffect(() => {
    // Schwellenwerte:
    //  - mobileBreakpoint 720px deckt sich mit der CSS-Media-Query in
    //    layout.tsx, in der der Spacer auf 56px reduziert wird.
    //  - heroThreshold 600px ist konservativ unterhalb der Hero-CTA-
    //    Position (Hero ist ~clamp(40-72)px Top-Padding + Headline +
    //    Mega-37 + CTA; CTA verschwindet erfahrungsgemaess zwischen
    //    500-700px Scroll). Wir nehmen 600px als verlaesslichen Trigger.
    const mobileBreakpoint = 720;
    const heroThreshold = 600;

    const compute = () => {
      const isMobile = window.innerWidth <= mobileBreakpoint;
      if (!isMobile) {
        // Desktop: kein Doppel-CTA-Problem -> Bar-CTA bleibt sichtbar.
        setCtaVisible(true);
        return;
      }
      setCtaVisible(window.scrollY > heroThreshold);
    };

    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, []);

  // prefers-reduced-motion: instantaner Toggle. Wir lesen das ein Mal
  // beim Mount und cachen es; mid-session-Wechsel sind extrem selten.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const transition = reduceMotion
    ? "none"
    : "opacity 200ms ease, transform 200ms ease";

  return (
    <div className="lp-sticky-bar is-active">
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <Link href="/" aria-label="Zur Startseite" style={{ display: "inline-flex", alignItems: "center" }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/brand/wordmark.svg" alt="First Class Professional" width={220} height={44} loading="eager" decoding="async" className="lp-sticky-bar-mark" style={{ height: 44, width: "auto" }} />
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* STREAM-F3: globaler Theme-Switch direkt neben dem Apply-CTA.
              Cookie-basiert, bleibt beim Sprung in den Funnel erhalten. */}
          <GlobalThemeSwitch />
          {/* Apply-CTA: scroll-getriggert auf Mobile (versteckt solange der
              Hero-CTA above-the-fold ist), permanent sichtbar auf Desktop.
              display: none statt opacity: 0, damit der Theme-Switch tat-
              saechlich rechtsbuendig ans Viewport-Ende rückt (sonst belegt
              der unsichtbare CTA noch Platz). */}
          <Link
            href={effectiveHref}
            onClick={startApply}
            className="fcp-btn-primary lp-glow-cta"
            style={{
              padding: "11px 22px",
              fontSize: 14,
              borderRadius: 9,
              display: ctaVisible ? "inline-flex" : "none",
              alignItems: "center",
              gap: 6,
              fontWeight: 600,
              background: "#1f7a4e",
              color: "#fff",
              transition,
            }}
            aria-hidden={ctaVisible ? undefined : true}
            tabIndex={ctaVisible ? undefined : -1}
          >
            Jetzt bewerben <IconArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
